/**
 * Access to private completion evidence — `/v1/admin/bookings/{code}/evidence*`.
 *
 * **Support holds nothing by default.** There is no hook here that lists evidence for a role;
 * the only way to see anything is a grant, and the only way to get one is `useGrantEvidence`.
 * That is the server's rule, and this module does not offer a shape that pretends otherwise.
 *
 * **Nothing is cached.** `useOpenEvidence` is a mutation, not a query, because the server writes
 * an access-log row before it hands the URL over: a cached URL would let a second operator open
 * a family's photographs under the first one's row. The links also expire in minutes, so a
 * remembered one is a button that silently stops working.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type GrantRow = components['schemas']['GrantRow'];
type GrantList = components['schemas']['GrantListResponse'];
type EvidenceList = components['schemas']['EvidenceListResponse'];

/**
 * Live and revoked, newest first. "Who has access" and "who has ever had access" differ.
 *
 * Takes a `string`, not `string | null`. The panel that calls it renders only inside a loaded
 * booking, so there is no such thing as a grants query without a code — and the `enabled` guard
 * plus the `?? ''` fallback that the nullable signature needed were both branches nothing could
 * reach, which is a claim nobody can check rather than a safety net.
 */
export function useEvidenceGrants(code: string) {
  return useQuery({
    queryKey: keys.bookings.evidenceGrants(code),
    queryFn: () => authedClient.request<GrantList>(`/v1/admin/bookings/${code}/evidence-grants`),
  });
}

function useGrantMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.bookings.all() }),
  });
}

/**
 * A named person, one booking, and an end.
 *
 * The window is not a parameter. It comes from `evidence.support_grant_hours`, so lengthening it
 * is a settings change with an audit row rather than a number typed into the form that creates
 * the grant being lengthened.
 */
export function useGrantEvidence() {
  return useGrantMutation(
    ({ code, adminId, reason }: { code: string; adminId: number; reason: string }) =>
      authedClient.request<GrantList>(`/v1/admin/bookings/${code}/evidence-grants`, {
        method: 'POST',
        body: { admin_id: adminId, reason },
      }),
  );
}

/** Takes effect on the next read. The row stays; the access stops. A reason is mandatory. */
export function useRevokeEvidenceGrant() {
  return useGrantMutation(({ grantId, reason }: { grantId: number; reason: string }) =>
    authedClient.request<GrantRow>(`/v1/admin/evidence-grants/${grantId}`, {
      method: 'DELETE',
      body: { reason },
    }),
  );
}

/** Mints fresh links under whatever grants are live for the caller. Every open is logged. */
export function useOpenEvidence() {
  return useMutation<EvidenceList, unknown, { code: string }>({
    mutationFn: ({ code }) =>
      authedClient.request<EvidenceList>(`/v1/admin/bookings/${code}/evidence`),
  });
}

/**
 * A superadmin's download for a legal hold (arch-12 §11). The reason is mandatory and is written
 * on its own audit row; the server issues a grant to the caller for exactly as long as the links
 * live, so this opens nothing for anybody afterwards.
 */
export function useLegalHoldEvidence() {
  return useMutation<EvidenceList, unknown, { code: string; reason: string }>({
    mutationFn: ({ code, reason }) =>
      authedClient.request<EvidenceList>(`/v1/admin/bookings/${code}/evidence/legal-hold`, {
        method: 'POST',
        body: { reason },
      }),
  });
}
